<template>
  <article class="language-container">
    <div class="wrapper" @mouseup="showLanguageDropDownList" @touchend.prevent="showLanguageDropDownList">
      <div class="icon"><img :src="getImagePath(selectedLanguage.icon)" alt="언어 아이콘" /></div>
      <div>
        <span class="text">{{ selectedLanguage.text }}</span>
      </div>
      <div class="caret">
        <div class="arrow" :class="{ up: !isShown, down: isShown }"></div>
      </div>
    </div>

    <ul v-show="isShown">
      <li
        v-for="(option, index) in languageDropDownList"
        :key="index"
        @mouseup="handleLanguageClick(option.value)"
        @touchend.prevent="handleLanguageClick(option.value)"
      >
        <div class="icon"><img :src="getImagePath(option.icon)" alt="언어 아이콘" /></div>
        <span class="text">{{ option.text }}</span>
      </li>
    </ul>

    <div
      v-if="isShown"
      class="backdrop"
      @mouseup="hideLanguageDropDownList"
      @touchend.prevent="hideLanguageDropDownList"
    ></div>
  </article>
</template>

<script setup lang="ts">
  import { LocaleLanguage } from '@/enums/LocaleLanguage';
  import { useConfigStore } from '@/stores/config';
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  const i18n = useI18n();
  const configStore = useConfigStore();

  // 언어 리스트
  const languages = [
    { value: LocaleLanguage.Ko, text: '한국어', icon: 'icon-language-ko.png' },
    { value: LocaleLanguage.En, text: 'English', icon: 'icon-language-en.png' },
    { value: LocaleLanguage.Ja, text: '日本語', icon: 'icon-language-ja.png' },
    { value: LocaleLanguage.Zh, text: '中文', icon: 'icon-language-zh.png' },
  ];

  if (configStore.config.CultureName === 'vi-VN') {
    languages.push({ value: LocaleLanguage.Vi, text: 'Tiếng Việt', icon: 'icon-language-vi.png' });
  }

  // 언어 리스트 show/hide
  const isShown = ref(false);

  // 선택된 언어
  const selectedLanguage = computed(() => {
    return languages.find((item) => item.value === i18n.locale.value) || languages[0];
  });

  // 선택된 언어를 제외한 언어 리스트
  const languageDropDownList = computed(() => {
    return (
      languages.filter((item) => {
        return item.value !== i18n.locale.value;
      }) || []
    );
  });

  const getImagePath = (value: string) => {
    return new URL(`/src/assets/images/${value}`, import.meta.url).href;
  };

  /** 언어 드랍다운리스트 열기 */
  const showLanguageDropDownList = () => {
    isShown.value = true;
  };

  /** 언어 드랍다운리스트 닫기 */
  const hideLanguageDropDownList = () => {
    isShown.value = false;
  };

  /**
   * 언어 드랍다운리스트 클릭
   * @param languageCode 선택한 언어 코드
   */
  const handleLanguageClick = (languageCode: LocaleLanguage) => {
    i18n.locale.value = languageCode;
    isShown.value = false;
  };
</script>

<style scoped lang="scss">
  @use '@/assets/scss/variables' as vars;

  .language-container {
    display: flex;
    align-items: center;
    position: relative;
    padding: 0 0.7031vw;
    height: 100%;
    border: 1px solid #e7e7e7;
    border-radius: 0.4688vw;
    cursor: pointer;

    .icon {
      display: flex;
      align-items: center;
      img {
        width: 3.2813vw;
        height: 2.1875vw;
      }
    }

    .text {
      display: flex;
      align-items: center;
      font-size: 1.5625vw;
      color: var(--header-text-secondary);
    }

    .wrapper {
      position: relative;
      display: grid;
      grid-template-columns: 3.2813vw 1fr 1.25vw;
      align-items: center;
      gap: 0.7813vw;
      width: 15.625vw;
      z-index: 4;

      .caret {
        display: flex;
        align-items: center;
        justify-content: center;

        .arrow {
          display: inline-block;
          padding: 0.2344vw;
          aspect-ratio: 1/1;
          border: solid;
          border-width: 0 0.0781vw 0.0781vw 0;
          border-color: var(--header-text-secondary);

          &.down {
            transform: rotate(45deg);
            -webkit-transform: rotate(45deg);
            margin-top: -25%;
          }
          &.up {
            transform: rotate(-135deg);
            transform: translateY(-50%);
            -webkit-transform: rotate(-135deg);
            margin-top: 25%;
          }
        }
      }
    }

    ul {
      position: absolute;
      top: 3.3594vw;
      left: 0;
      display: grid;
      grid-template-rows: auto;
      gap: 1.0938vw;
      width: 15.625vw;
      padding: 0.5469vw 0.7031vw;
      background-color: var(--header-bg);
      border: 1px solid #e7e7e7;
      border-radius: 0.4688vw;
      list-style: none;
      cursor: pointer;
      z-index: 4;

      li {
        display: grid;
        grid-template-columns: 3.2813vw 1fr;
        gap: 0.7813vw;
      }
    }

    .backdrop {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: calc(var(--vh, 1vh) * 100);
      z-index: 2;
    }
  }
</style>
